import { BaseApi } from '@/apis/base.api';
import { resolveDefectPanelContext } from '@/utils/map/resolveDefectPanelContext';
import {
  findModuleInSiteDesignHierarchy,
  logModuleListDebug
} from './moduleListSyncDebug';

const moduleInfoCache = new Map();

function extractData(response) {
  return response?.data?.content ?? response?.data ?? response;
}

function isHalLink(value) {
  return (
    value &&
    typeof value === 'object' &&
    typeof value.href === 'string' &&
    value.moduleId == null &&
    value.stringId == null &&
    value.arrayId == null
  );
}

async function fetchHalEntity(hal) {
  if (!hal?.href) return null;
  const match = hal.href.match(/\/api(\/.*)$/);
  const path = match ? match[1] : hal.href;
  try {
    const response = await BaseApi.get(path);
    return extractData(response);
  } catch {
    return null;
  }
}

async function expandSolarModuleGraph(solarModule) {
  if (!solarModule || typeof solarModule !== 'object') {
    return solarModule;
  }

  let expanded = { ...solarModule };
  if (isHalLink(expanded)) {
    const fetched = await fetchHalEntity(expanded);
    if (!fetched) return solarModule;
    expanded = fetched;
  }

  if (expanded.solarString && isHalLink(expanded.solarString)) {
    expanded.solarString = await fetchHalEntity(expanded.solarString);
  }

  if (expanded.solarString?.solarArray && isHalLink(expanded.solarString.solarArray)) {
    expanded.solarString = {
      ...expanded.solarString,
      solarArray: await fetchHalEntity(expanded.solarString.solarArray)
    };
  }

  return expanded;
}

function mergeModuleInfoWithSiteDesign(info, siteDesignHierarchy) {
  if (!info?.solarModuleSeq || !siteDesignHierarchy?.length) return info;

  const fromDesign = findModuleInSiteDesignHierarchy(siteDesignHierarchy, info.solarModuleSeq);
  if (!fromDesign) return info;

  return {
    ...info,
    moduleId: info.moduleId ?? fromDesign.moduleDisplayId,
    solarStringSeq: info.solarStringSeq ?? fromDesign.stringSeq ?? null,
    stringId: info.stringId ?? fromDesign.stringDisplayId ?? null,
    solarArraySeq: info.solarArraySeq ?? fromDesign.arraySeq ?? null,
    arrayId: info.arrayId ?? fromDesign.arrayDisplayId ?? null
  };
}

async function fetchModuleHierarchyInfo(moduleSeq, siteDesignHierarchy) {
  const key = String(moduleSeq);
  if (moduleInfoCache.has(key)) {
    const cached = moduleInfoCache.get(key);
    logModuleListDebug('enrich.fetchModule.cacheHit', { moduleSeq: key, info: cached });
    return cached;
  }

  logModuleListDebug('enrich.fetchModule.start', { moduleSeq: key });
  try {
    const response = await BaseApi.get(`/solarModules/${moduleSeq}`);
    const solarModule = await expandSolarModuleGraph(extractData(response));
    const ctx = resolveDefectPanelContext({ defect: { solarModuleSeq: moduleSeq }, solarModule });
    const stringNode = solarModule?.solarString;
    const arrayNode = stringNode?.solarArray ?? solarModule?.solarArray;

    let info = {
      solarModuleSeq: moduleSeq,
      moduleId: ctx.moduleId,
      solarStringSeq: stringNode?.id ?? stringNode?.seq ?? null,
      stringId: ctx.stringId,
      solarArraySeq: arrayNode?.id ?? arrayNode?.seq ?? null,
      arrayId: ctx.arrayId
    };
    info = mergeModuleInfoWithSiteDesign(info, siteDesignHierarchy);
    moduleInfoCache.set(key, info);
    logModuleListDebug('enrich.fetchModule.ok', {
      moduleSeq: key,
      info,
      rawHasString: !!stringNode,
      rawHasArray: !!arrayNode,
      mergedFromSiteDesign:
        (!stringNode || !arrayNode) &&
        (info.solarStringSeq != null || info.solarArraySeq != null)
    });
    return info;
  } catch (error) {
    logModuleListDebug('enrich.fetchModule.fail', {
      moduleSeq: key,
      message: error?.message ?? String(error),
      status: error?.response?.status ?? null
    });
    moduleInfoCache.set(key, null);
    return null;
  }
}

/**
 * 모듈 API 응답 목록 → siteDesignModuleHierarchy 형식
 * (buildModuleTreeFromSiteDesign 입력과 동일)
 */
export function buildHierarchyFromModuleInfos(infos) {
  const arrayMap = new Map();

  for (const info of infos || []) {
    if (!info?.solarModuleSeq) continue;

    const arrayKey = String(info.solarArraySeq ?? '__null__');
    if (!arrayMap.has(arrayKey)) {
      arrayMap.set(arrayKey, {
        seq: info.solarArraySeq ?? null,
        displayId: info.arrayId ?? null,
        strings: new Map()
      });
    }

    const arrayNode = arrayMap.get(arrayKey);
    const stringKey = String(info.solarStringSeq ?? '__null__');
    if (!arrayNode.strings.has(stringKey)) {
      arrayNode.strings.set(stringKey, {
        seq: info.solarStringSeq ?? null,
        displayId: info.stringId ?? null,
        modules: []
      });
    }

    arrayNode.strings.get(stringKey).modules.push({
      seq: info.solarModuleSeq,
      displayId: info.moduleId ?? String(info.solarModuleSeq)
    });
  }

  const arrays = [...arrayMap.values()].map(arrayNode => ({
    seq: arrayNode.seq,
    displayId: arrayNode.displayId,
    strings: [...arrayNode.strings.values()].map(stringNode => ({
      seq: stringNode.seq,
      displayId: stringNode.displayId,
      modules: stringNode.modules.sort((a, b) => {
        const aid = a.displayId != null ? Number(a.displayId) : NaN;
        const bid = b.displayId != null ? Number(b.displayId) : NaN;
        if (!Number.isNaN(aid) && !Number.isNaN(bid)) return aid - bid;
        return String(a.displayId ?? '').localeCompare(String(b.displayId ?? ''));
      })
    }))
  }));

  arrays.sort((a, b) => {
    const aid = a.displayId != null ? Number(a.displayId) : NaN;
    const bid = b.displayId != null ? Number(b.displayId) : NaN;
    if (!Number.isNaN(aid) && !Number.isNaN(bid)) return aid - bid;
    if (!Number.isNaN(aid)) return -1;
    if (!Number.isNaN(bid)) return 1;
    return String(a.displayId ?? '').localeCompare(String(b.displayId ?? ''));
  });

  return arrays;
}

function applyModuleInfoToDefect(defect, info) {
  if (!info) return { defect, changed: false };

  const updated = { ...defect };
  let changed = false;

  const assign = (field, value) => {
    if ((updated[field] == null || updated[field] === '') && value != null && value !== '') {
      updated[field] = value;
      changed = true;
    }
  };

  assign('moduleId', info.moduleId);
  assign('solarStringSeq', info.solarStringSeq);
  assign('stringId', info.stringId);
  assign('solarArraySeq', info.solarArraySeq);
  assign('arrayId', info.arrayId);

  return { defect: updated, changed };
}

/**
 * siteDesign 없을 때 solarModules API로 결함·트리 계층 보강
 * @param {object[]} defects
 * @returns {Promise<{ rows: object[], changed: boolean, hierarchy: object[] }>}
 */
export async function enrichWfsDefectRowsFromSolarModules(defects, { siteDesignHierarchy } = {}) {
  const list = Array.isArray(defects) ? defects : [];
  const uniqueSeqs = [
    ...new Set(
      list
        .map(d => d?.solarModuleSeq)
        .filter(seq => seq != null && seq !== '')
        .map(seq => String(seq))
    )
  ];

  logModuleListDebug('enrich.start', {
    defectCount: list.length,
    uniqueModuleSeqCount: uniqueSeqs.length,
    uniqueModuleSeqs: uniqueSeqs.slice(0, 20),
    withoutModuleSeq: list.filter(d => d?.solarModuleSeq == null || d?.solarModuleSeq === '').length
  });

  if (!uniqueSeqs.length) {
    logModuleListDebug('enrich.skip', { reason: 'no solarModuleSeq on defects' });
    return { rows: list, changed: false, hierarchy: [] };
  }

  const infos = await Promise.all(
    uniqueSeqs.map(seq => fetchModuleHierarchyInfo(seq, siteDesignHierarchy))
  );
  const infoBySeq = new Map();
  const failedSeqs = [];
  uniqueSeqs.forEach((seq, index) => {
    if (infos[index]) {
      infoBySeq.set(seq, infos[index]);
    } else {
      failedSeqs.push(seq);
    }
  });

  let changed = false;
  const enrichedSamples = [];
  const rows = list.map(defect => {
    const info = infoBySeq.get(String(defect?.solarModuleSeq));
    const { defect: updated, changed: rowChanged } = applyModuleInfoToDefect(defect, info);
    if (rowChanged) {
      changed = true;
      if (enrichedSamples.length < 5) {
        enrichedSamples.push({
          defectSeq: defect.seq,
          before: {
            moduleId: defect.moduleId,
            arrayId: defect.arrayId,
            stringId: defect.stringId
          },
          after: {
            moduleId: updated.moduleId,
            arrayId: updated.arrayId,
            stringId: updated.stringId,
            solarArraySeq: updated.solarArraySeq,
            solarStringSeq: updated.solarStringSeq
          }
        });
      }
    }
    return updated;
  });

  const hierarchy = buildHierarchyFromModuleInfos([...infoBySeq.values()]);

  logModuleListDebug('enrich.done', {
    changed,
    fetchedOk: infoBySeq.size,
    fetchedFail: failedSeqs.length,
    failedSeqs: failedSeqs.slice(0, 10),
    enrichedSamples,
    hierarchySummary: {
      arrayCount: hierarchy.length,
      arrays: hierarchy.map(a => ({
        seq: a.seq,
        displayId: a.displayId,
        strings: (a.strings || []).map(s => ({
          seq: s.seq,
          displayId: s.displayId,
          modules: (s.modules || []).map(m => ({ seq: m.seq, displayId: m.displayId }))
        }))
      }))
    }
  });

  return { rows, changed, hierarchy };
}

/** 사이트 전환·테스트 시 캐시 초기화 */
export function clearModuleHierarchyInfoCache() {
  moduleInfoCache.clear();
}
